import { isValidTimezone } from './userTimezone'
import { createClient } from '@supabase/supabase-js'
import type { User } from '@supabase/supabase-js'
import type { Tables } from '../types/supabase'
import { Database, TeamRole } from '../types/supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
}

// Capture before the auth SDK consumes/clears the OAuth URL. Keep it in memory only.
let slackCallbackProviderToken = typeof window === 'undefined' ? null
  : new URLSearchParams(window.location.hash?.slice(1) || '').get('provider_token')

// Create default client with anon key - now with Auth support
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// The SDK can deliver OAuth credentials through its session event before React
// reads the session. Capture synchronously; never call Auth APIs inside this callback.
const { data: providerTokenListener } = supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') slackCallbackProviderToken = null
  else if (session?.provider_token && session.user.app_metadata?.provider === 'slack_oidc') {
    slackCallbackProviderToken = session.provider_token
  }
})
if (import.meta.hot) import.meta.hot.dispose(() => providerTokenListener.subscription.unsubscribe())

// ============================================================================
// SLACK-ONLY AUTHENTICATION FUNCTIONS
// ============================================================================

// Slack sign-in tokens are transient; refreshed app sessions may no longer contain one.
// Name lookup still works with an installed bot, without persisting provider tokens.
export async function refreshSlackWorkspaceName(slackTeamId: string, providerToken?: string | null): Promise<string | null> {
  let token = providerToken || slackCallbackProviderToken
  if (!token) {
    const { data } = await supabase.auth.getSession()
    token = data.session?.provider_token || null
  }
  try {
    const { data, error } = await supabase.functions.invoke('resolve-slack-workspace-name', {
      body: { slack_team_id: slackTeamId, ...(token ? { provider_token: token } : {}) },
    })
    if (error) throw new Error('Unable to refresh the Slack workspace name')
    return data?.resolved === true && data.slack_team_id === slackTeamId && typeof data.name === 'string' && data.name.trim()
      ? data.name.trim() : null
  } finally {
    if (token === slackCallbackProviderToken) slackCallbackProviderToken = null
  }
}

// Slack Authentication - Using Supabase's native Slack OIDC provider
export const signInWithSlack = async (redirectTo?: string) => {
  // Determine the correct redirect URL
  const currentUrl = window.location.origin
  const isLocalhost = currentUrl.includes('localhost')
  const baseUrl = isLocalhost ? window.location.origin : 'https://app.syncsimple.ai'
  const defaultRedirect = `${baseUrl}/dashboard`
  
  // console.log('signInWithSlack: Using Supabase native Slack OIDC')
  // console.log('signInWithSlack: Redirect URL:', redirectTo || defaultRedirect)
  
  // Use Supabase's native Slack OIDC authentication
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'slack_oidc',
    options: {
      redirectTo: redirectTo || defaultRedirect,
      queryParams: {
        prompt: 'login'
      }
    }
  })
  
  if (error) {
    console.error('Slack OIDC authentication error:', error)
    throw error
  }
  
  return data
}

// Setup user and team after Slack authentication - simplified for OIDC
export const setupSlackUserAndTeam = async (plan?: string, providerToken?: string | null) => {
  const user = await getCurrentUser()
  if (!user) {
    throw new Error('No authenticated user found')
  }

  console.log('Setting up Slack user and team for:', user.email)
  console.log('User metadata:', user.user_metadata)
  console.log('App metadata:', user.app_metadata)
  console.log('Custom claims:', user.user_metadata?.custom_claims)

  // Extract Slack data from OIDC claims
  const slackTeamId = user.user_metadata?.custom_claims?.['https://slack.com/team_id'] || 
                      user.user_metadata?.['https://slack.com/team_id']
  const slackUserId = user.user_metadata?.custom_claims?.['https://slack.com/user_id'] || 
                      user.user_metadata?.['https://slack.com/user_id'] ||
                      user.user_metadata?.sub ||
                      user.user_metadata?.provider_id
  const slackUserName = user.user_metadata?.custom_claims?.['https://slack.com/user_name'] ||
                        user.user_metadata?.['https://slack.com/user_name'] ||
                        user.user_metadata?.name ||
                        user.user_metadata?.full_name ||
                        user.email?.split('@')[0] || 'Unknown'

  // Avatar: OIDC's standard `profile` scope claim is `picture` — Supabase's
  // slack_oidc provider is expected to surface it directly on user_metadata
  // (same place `name`/`full_name` land above), not nested under
  // custom_claims like the Slack-specific team/user id claims are. NOT
  // independently verified against a live signed-in session in this
  // environment (no Slack workspace to authenticate through here) — the
  // `console.log('User metadata:', user.user_metadata)` a few lines up
  // prints the real shape on first sign-in; if `picture` isn't actually
  // there, check that log for whatever Slack/Supabase actually calls it
  // (e.g. a `https://slack.com/user_image_192`-style custom claim) and add
  // it to this fallback chain the same way the claims above do.
  const slackAvatarUrl = user.user_metadata?.picture ||
                         user.user_metadata?.avatar_url ||
                         user.user_metadata?.custom_claims?.picture ||
                         user.user_metadata?.custom_claims?.['https://slack.com/user_image_512'] ||
                         user.user_metadata?.['https://slack.com/user_image_512'] ||
                         user.user_metadata?.custom_claims?.['https://slack.com/user_image_192'] ||
                         user.user_metadata?.['https://slack.com/user_image_192'] ||
                         null

  // Slack may provide the workspace name in OIDC claims. Never persist a
  // person's name as the shared workspace name when that claim is absent.
  let slackTeamName = user.user_metadata?.custom_claims?.['https://slack.com/team_name'] ||
                      user.user_metadata?.['https://slack.com/team_name'] ||
                      'Your workspace'

  console.log('Extracted Slack data:', { slackTeamId, slackTeamName, slackUserId, slackUserName })

  if (!slackTeamId) {
    console.error('No Slack team ID found in user metadata')
    throw new Error('No Slack team ID found in user metadata')
  }

  if (!slackUserId) {
    console.error('No Slack user ID found in user metadata')
    throw new Error('No Slack user ID found in user metadata')
  }

  try {
    // First, try to find existing team to get the real team name
    const { data: existingTeam } = await supabase
      .from('teams')
      .select('name')
      .eq('slack_team_id', slackTeamId)
      .single()
    
    if (existingTeam?.name) {
      slackTeamName = existingTeam.name
      console.log('Found existing team name:', slackTeamName)
    }


    // Find or create the team
    console.log('Finding/creating team for Slack workspace:', { slackTeamId, teamName: slackTeamName, plan })
    
    const { data: teamResult, error: teamFunctionError } = await supabase.rpc(
      'find_or_create_slack_team',
      {
        p_slack_team_id: slackTeamId,
        p_team_name: slackTeamName,
        p_auth_user_id: user.id,
        p_plan: plan || 'trial' // Default to trial if no plan specified
      } as any
    )

    console.log('RPC result:', { teamResult, teamFunctionError })

    if (teamFunctionError) {
      console.error('Error in find_or_create_slack_team:', teamFunctionError)
      throw teamFunctionError
    }

    if (!teamResult) {
      console.error('No team returned from find_or_create_slack_team')
      throw new Error('Failed to create or find team')
    }

    // The RPC function returns the result nested under the function name
    // Handle both possible response structures
    let functionResult: any
    if (typeof teamResult === 'object' && 'find_or_create_slack_team' in teamResult) {
      functionResult = (teamResult as any).find_or_create_slack_team
    } else {
      functionResult = teamResult
    }
    
    console.log('Function result:', functionResult)

    // Now extract the team data from the result
    const teamData = functionResult.team
    const isNewTeam = functionResult.is_new_team
    const userRole = functionResult.user_role

    if (!teamData || !teamData.id) {
      console.error('No team data in result:', functionResult)
      throw new Error('Invalid response from team creation function')
    }

    // Resolve after the team exists so the authoritative name is persisted,
    // including when two people sign up concurrently. Both Slack IDs are verified.
    try {
      const resolvedName = await refreshSlackWorkspaceName(slackTeamId, providerToken)
      if (resolvedName) teamData.name = resolvedName
    } catch {
      console.warn('Workspace name lookup unavailable; keeping the existing name or neutral fallback.')
    }

    console.log('Team created/found:', { teamId: teamData.id, isNewTeam, userRole })

    // Create or update the user record using the database function
    console.log('Creating/updating user record using link_slack_user_to_auth function...')
    
    const { data: linkResult, error: linkError } = await (supabase as any)
      .rpc('link_slack_user_to_auth', {
        p_auth_user_id: user.id,
        p_slack_user_id: slackUserId,
        p_team_id: teamData.id,
        p_user_name: slackUserName,
        p_user_email: user.email,
        p_avatar_url: slackAvatarUrl
      }) as { data: { success: boolean; user_id: string; action: string } | null; error: any }
    
    if (linkError) {
      console.error('Error linking user:', linkError)
      throw linkError
    }
    
    console.log('User link result:', linkResult)
    
    if (!linkResult?.success || !linkResult?.user_id) {
      console.error('Failed to link user:', linkResult)
      throw new Error('Failed to link Slack user to auth user')
    }
    
    // Get the user data
    const { data: userData, error: userFetchError } = await supabase
      .from('users')
      .select()
      .eq('id', linkResult.user_id)
      .single()
    
    if (userFetchError || !userData) {
      console.error('Error fetching linked user:', userFetchError)
      throw new Error('Failed to fetch user after linking')
    }

    console.log('User record created/updated:', userData)

    if (!userData || !userData.id) {
      console.error('Failed to create or retrieve user data')
      throw new Error('Failed to create or retrieve user data')
    }

    // Create team membership if it doesn't exist
    console.log('Creating/updating team membership...')
    
    // Check if membership already exists
    const { data: existingMembership } = await supabase
      .from('team_members')
      .select()
      .eq('team_id', teamData.id)
      .eq('auth_user_id', user.id)
      .single()

    if (!existingMembership) {
      // If an admin already chose a role for this person when adding them
      // (Team Management's invite flow — invite-team-members sets
      // users.pending_role, since team_members can't exist yet: it requires
      // auth_user_id NOT NULL and this person hasn't signed in until right
      // now), honor that instead of the "default member" default. Nobody who predates this feature ever has a
      // pending_role, so this falls through to the exact original behavior
      // for every other sign-in.
      const effectiveRole = (userData as { pending_role?: string | null }).pending_role || 'member'

      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: teamData.id,
          auth_user_id: user.id,
          user_id: userData.id,
          role: effectiveRole,
          joined_at: new Date().toISOString()
        })

      if (memberError) {
        console.error('Error creating team membership:', memberError)
        throw memberError
      }
      console.log('Team membership created successfully')

      // Best-effort cleanup — a pending_role is one-time-use. Not fatal if
      // this fails; it would just leave a stale (harmless, unread-again)
      // value behind.
      if ((userData as { pending_role?: string | null }).pending_role) {
        await (supabase as any)
          .from('users')
          .update({ pending_role: null })
          .eq('id', userData.id)
      }
    } else {
      console.log('Team membership already exists')
    }

    // Await notification acceptance after owner details exist. Failure never blocks signup.
    if (isNewTeam && teamData.status === 'pending_review') {
      try {
        const { error: notifyError } = await supabase.functions.invoke('notify-pending-team', {
          body: { teamId: teamData.id }
        })
        if (notifyError) console.error('Workspace approval notification failed')
      } catch {
        console.error('Workspace approval notification failed')
      }
    }

    console.log('Slack user and team setup completed successfully')
    return { user: userData, team: teamData }
  } catch (error) {
    console.error('Error in setupSlackUserAndTeam:', error)
    throw error
  }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

export const getCurrentSession = async () => {
  // console.log('getCurrentSession: Starting...')
  try {
    // console.log('getCurrentSession: About to call getSession...')
    const { data: { session }, error } = await supabase.auth.getSession()
    
    // console.log('getCurrentSession: Got response:', { session: session ? 'exists' : 'null', error })
    if (error) throw error
    return session
  } catch (error) {
    console.error('getCurrentSession: Error:', error)
    throw error
  }
}

// Team Management Functions
export const getUserTeams = async (user?: User): Promise<Tables<'team_members'>[]> => {
  const currentUser = user || await getCurrentUser()
  if (!currentUser) {
    throw new Error('No authenticated user found')
  }

  const { data, error } = await supabase
    .from('team_members')
    .select(`
      *,
      teams:teams(id, slack_team_id, name, plan, created_at, updated_at, standup_config, app_uninstalled_at, status, has_slack_bot_token)
    `)
    .eq('auth_user_id', currentUser.id)
    .order('joined_at', { ascending: false })

  if (error) {
    console.error('Error fetching user teams:', error)
    throw error
  }

  return data || []
}

export const getTeamMembers = async (teamId: string) => {
  // console.log('getTeamMembers: Starting query for team:', teamId)
  
  // Get all team members for this team with user details using the view
  // RLS policies ensure users can only see teams they're members of
  const { data: allMembers, error } = await supabase
    .from('team_members_with_users')
    .select('*')
    .eq('team_id', teamId)
    .order('joined_at', { ascending: true })
  
  if (error) {
    console.error('getTeamMembers: Error:', error)
    throw error
  }
  
  // console.log('getTeamMembers: Got members:', allMembers)
  
  // Transform the data to match the expected format
  const transformedMembers = allMembers?.map(member => ({
    ...member,
    users: {
      name: member.user_name,
      email: member.user_email
    }
  }))
  
  return transformedMembers
}

export const getUserRole = async (teamId: string) => {
  const user = await getCurrentUser()
  if (!user) return null
  
  const { data, error } = await supabase
    .from('team_members')
    .select('role')
    .eq('team_id', teamId)
    .eq('auth_user_id', user.id)
    .single()
  
  if (error) throw error
  return data?.role as TeamRole
}

export const checkUserTeamAccess = async (teamId: string): Promise<{ hasAccess: boolean, role?: TeamRole }> => {
  const user = await getCurrentUser()
  if (!user) return { hasAccess: false }
  
  const { data, error } = await supabase
    .from('team_members')
    .select('role')
    .eq('team_id', teamId)
    .eq('auth_user_id', user.id)
    .single()
  
  if (error || !data) return { hasAccess: false }
  return { hasAccess: true, role: data.role as TeamRole }
}

// Team Invitation Functions
export const inviteUserToTeam = async (teamId: string, email: string, role: TeamRole = 'member') => {
  const user = await getCurrentUser()
  if (!user) throw new Error('User not authenticated')
  
  const session = await getCurrentSession()
  if (!session) throw new Error('No session found')
  
  // First create the invitation record
  const { data, error } = await supabase
    .from('team_invitations')
    .insert({
      team_id: teamId,
      email,
      role,
      invited_by: user.id
    })
    .select()
    .single()
  
  if (error) throw error
  
  // Then send the invitation email
  const { error: emailError } = await supabase.functions.invoke('send-team-invitation', {
    body: {
      teamId,
      email,
      role,
      invitedBy: user.id
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  })
  
  if (emailError) {
    console.error('Failed to send invitation email:', emailError)
    // Don't throw - the invitation was created, just the email failed
  }
  
  // Update Stripe subscription quantity if team has an active subscription
  try {
    await supabase.functions.invoke('update-subscription-quantity', {
      body: { teamId }
    })
  } catch (error) {
    console.error('Failed to update subscription quantity:', error)
    // Don't throw - this is not critical to the invitation flow
  }
  
  return data
}

export const acceptTeamInvitation = async (token: string) => {
  const { data, error } = await supabase.rpc('accept_team_invitation', {
    invitation_token: token
  })
  
  if (error) throw error
  return data
}

export const getTeamInvitations = async (teamId: string) => {
  const { data, error } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('team_id', teamId)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
  
  if (error) throw error
  return data
}

export const getUserInvitations = async () => {
  const user = await getCurrentUser()
  if (!user?.email) {
    // console.log('getUserInvitations: No user email found')
    return []
  }
  
  // console.log('getUserInvitations: Getting invitations for:', user.email)
  
  // Get user's invitations
  const { data: invitations, error: invitationError } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('email', user.email)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
  
  if (invitationError) {
    console.error('getUserInvitations: Error fetching invitations:', invitationError)
    throw invitationError
  }
  
  // console.log('getUserInvitations: Got invitations:', invitations)
  
  if (!invitations || invitations.length === 0) {
    return []
  }
  
  // Get team names for the invitations
  const teamIds = invitations.map(inv => inv.team_id)
  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('id, name')
    .in('id', teamIds)
  
  if (teamsError) {
    console.error('getUserInvitations: Error fetching team names:', teamsError)
    // Don't throw error, just return invitations without team names
    return invitations.map(inv => ({ ...inv, teams: null }))
  }
  
  // Combine data
  const result = invitations.map(invitation => ({
    ...invitation,
    teams: teams?.find(team => team.id === invitation.team_id) || null
  }))
  
  // console.log('getUserInvitations: Final result:', result)
  return result
}

export const updateTeamMemberRole = async (memberId: string, role: TeamRole) => {
  // console.log('updateTeamMemberRole: Starting update for member:', memberId, 'to role:', role)
  
  const { data, error } = await supabase
    .from('team_members')
    .update({ role })
    .eq('id', memberId)
    .select()
  
  // console.log('updateTeamMemberRole: Response:', { data, error })
  
  if (error) {
    console.error('updateTeamMemberRole: Error updating role:', error)
    throw error
  }
  
  // console.log('updateTeamMemberRole: Successfully updated role')
  return data
}

export const removeTeamMember = async (memberId: string) => {
  const { error } = await supabase
    .from('team_members')
    .delete()
    .eq('id', memberId)
  
  if (error) throw error
}

// ============================================================================
// EXISTING FUNCTIONS (Updated for new auth system)
// ============================================================================

// Check if team has a bot token for slash commands. The token itself is not
// readable from the browser (20261002 RLS hardening) -- only whether it exists.
export const checkTeamBotTokens = async (teamId: string) => {
  const { data: team, error } = await supabase
    .from('teams')
    .select('has_slack_bot_token, slack_team_id, app_uninstalled_at')
    .eq('id', teamId)
    .single()

  if (error) throw error

  return {
    hasBotToken: !!team?.has_slack_bot_token && !team?.app_uninstalled_at,
    slackTeamId: team?.slack_team_id
  }
}

// Generate Slack app installation URL
export const getSlackInstallUrl = async (slackTeamId?: string) => {
  const { data, error } = await supabase.functions.invoke('start-slack-install', {
    body: { slack_team_id: slackTeamId },
  })
  if (error || data?.error || typeof data?.url !== 'string') throw new Error(data?.error || 'Unable to connect Slack. Please retry.')
  if (data.connected && typeof data.teamId === 'string') return new URL(`/dashboard/team/${encodeURIComponent(data.teamId)}/config?section=schedule&slack=connected`, window.location.origin).toString()
  const url = new URL(data.url)
  if (url.origin !== 'https://slack.com' && url.origin !== window.location.origin) throw new Error('Unexpected Slack connection destination')
  return url.toString()
}

export const getTeamStandups = async (teamId: string, date?: string) => {
  // console.log('getTeamStandups: Making request for team:', teamId)
  let query = supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id
      )
    `)
    .eq('team_id', teamId)
    .order('created_at', { ascending: false })

  if (date) {
    query = query.eq('date', date)
  }

  const { data, error } = await query
  
  // console.log('getTeamStandups: Response:', { data, error })
  if (error) throw error
  return data
}

export const getStandupsByDateRange = async (
  teamId: string, 
  startDate: string, 
  endDate: string
) => {
  const { data, error } = await supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id
      )
    `)
    .eq('team_id', teamId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false })
  
  if (error) throw error
  return data
}

export const getStandupsGroupedByDate = async (teamId: string, limit: number = 14) => {
  const { data, error } = await supabase
    .from('standup_responses')
    .select(`
      *,
      users (
        name,
        slack_user_id,
        timezone
      )
    `)
    .eq('team_id', teamId)
    .order('date', { ascending: false })
    .limit(limit * 10) // Get more entries to account for grouping
  
  if (error) throw error
  
  // Debug logging to see what's in the database
  // console.log('=== DATABASE STANDUP DATA DEBUG ===')
  data?.slice(0, 3).forEach((standup, i) => {
    // Parse date as local date to avoid timezone conversion
    const parsedDate = standup.date ? new Date(standup.date + 'T00:00:00') : null
    // console.log(`Standup ${i + 1}:`, {
    //   id: standup.id,
    //   rawDate: standup.date,
    //   rawCreatedAt: standup.created_at,
    //   parsedDate: parsedDate,
    //   parsedCreatedAt: standup.created_at ? new Date(standup.created_at) : null,
    //   localDateString: parsedDate ? parsedDate.getFullYear() + '-' + 
    //     String(parsedDate.getMonth() + 1).padStart(2, '0') + '-' + 
    //     String(parsedDate.getDate()).padStart(2, '0') : null
    // })
  })
  // console.log('=======================================')
  
  // Group by date
  const groupedStandups = data?.reduce((acc, standup) => {
    const date = standup.date
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(standup)
    return acc
  }, {} as Record<string, typeof data>)
  
  // Convert to array and sort by date
  const sortedGroups = Object.entries(groupedStandups || {})
    .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
    .slice(0, limit)
  
  return sortedGroups
}

export const generateDailySummary = async (teamId: string, date: string) => {
  try {
    const { data: standups, error } = await supabase
      .from('standup_responses')
      .select(`
        *,
        users (
          name,
          slack_user_id
        )
      `)
      .eq('team_id', teamId)
      .eq('date', date)
    
    if (error) throw error
    if (!standups || standups.length === 0) {
      return null
    }
    
    // Extract all the standup content
    const standupContent = standups.map(standup => ({
      name: standup.users?.name || 'Unknown User',
      yesterday: (standup.questions as any)?.yesterday || '',
      today: (standup.questions as any)?.today || '',
      blockers: (standup.questions as any)?.blockers || '',
      sentiment: standup.sentiment || 0
    }))
    
    // Call edge function to generate summary
    const { data: summary, error: summaryError } = await supabase.functions.invoke('generate-daily-summary', {
      body: {
        standups: standupContent,
        date: date
      }
    })
    
    if (summaryError) {
      console.error('Error generating summary:', summaryError)
      return null
    }
    
    return summary
  } catch (error) {
    console.error('Error in generateDailySummary:', error)
    return null
  }
}

// null means automatic device timezone. Preserve existing saved overrides,
// including UTC, and tolerate more than one workspace profile for an account.
export const getUserTimezone = async (userId: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from('users')
    .select('timezone')
    .eq('auth_user_id', userId)
    .not('timezone', 'is', null)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data?.timezone ?? null
}

export const updateUserTimezone = async (userId: string, timezone: string | null) => {
  if (timezone !== null && !isValidTimezone(timezone)) throw new Error('Choose a valid timezone')
  const { data, error } = await supabase
    .from('users')
    .update({ timezone, updated_at: new Date().toISOString() })
    .eq('auth_user_id', userId)
    .select('id')

  if (error) throw error
  if (!data?.length) throw new Error('Your profile is not ready. Please reload and try again.')
  return data
}

export const uploadVoiceMemo = async (file: File, fileName: string) => {
  const { data, error } = await supabase.storage
    .from('voice-memos')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    })

  if (error) throw error
  return data
}

export const getVoiceMemoUrl = async (path: string) => {
  const { data, error } = await supabase.storage
    .from('voice-memos')
    .createSignedUrl(path, 3600) // 1 hour expiry

  if (error) throw error
  return data.signedUrl
}

// Import for team claiming
export const findUnclaimedTeams = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    // Find teams where user has a Slack user record but no team membership
    const { data: unclaimedUsers, error } = await supabase
      .from('users')
      .select(`
        id,
        slack_user_id,
        name,
        team_id,
        teams:teams(id, name, slack_team_id)
      `)
      .is('auth_user_id', null)
    
    if (error) throw error

    // Filter to teams that don't already have this auth user as a member
    const { data: existingMemberships } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('auth_user_id', user.id)
    
    const existingTeamIds = new Set(existingMemberships?.map(m => m.team_id) || [])
    
    return unclaimedUsers?.filter(u => 
      u.teams && u.team_id && !existingTeamIds.has(u.team_id)
    ) || []
    
  } catch (error) {
    console.error('Error finding unclaimed teams:', error)
    return []
  }
}

export const claimTeam = async (userRecordId: string, teamId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Update the users record to link to auth account
    const { error: userError } = await supabase
      .from('users')
      .update({ auth_user_id: user.id })
      .eq('id', userRecordId)
    
    if (userError) throw userError

    // Create team membership
    const { error: membershipError } = await supabase
      .from('team_members')
      .insert({
        team_id: teamId,
        auth_user_id: user.id,
        user_id: userRecordId,
        role: 'admin', // Claiming user gets admin role
        joined_at: new Date().toISOString()
      })
    
    if (membershipError) throw membershipError

    return true
  } catch (error) {
    console.error('Error claiming team:', error)
    throw error
  }
}
